// shared/ui/ImagenGuardada.jsx  (miniatura presentacional de una imagen YA guardada, con los estados de la eliminación diferida)
// La usan el formulario (ImagenesSection, 120px, "al guardar") y el detalle (150px, "al presionar «Eliminar»").
// media.eliminacion (application): undefined | { estado: 'marked' | 'deleting' | 'error', error }.
// marked/error: atenuada (gris) + "Deshacer" en la esquina de la papelera; deleting: velo + spinner; error: borde rojo + mensaje.
// Con `abrir`, sin canDelete ni marca y con url, la miniatura abre la imagen completa en otra pestaña. `saliendo` la desvanece (ui, reduced-motion aware).
import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import env from '../../../../environment.js';
import { DISPARADOR_GUARDAR, MSG_ERROR_ELIMINACION, mediaUrl, tituloDeshacerEliminar } from '../domain/media';
import { BRAND_GREEN, ERROR_BORDER, deleteBtnSx, veilSx } from './trashBadgeSx';

const FADE_MS = 200;

const imgStyle = { width: '100%', height: '100%', objectFit: 'cover', display: 'block' };
const dimmedImgStyle = { ...imgStyle, opacity: 0.55, filter: 'grayscale(1)' };

const ImagenGuardada = ({
  media,
  alt = 'Evidencia guardada',
  size = 120,
  canDelete = false,
  abrir = false,
  disabled = false,
  saliendo = false,
  disparador = DISPARADOR_GUARDAR,
  onDelete,
  onUndo,
}) => {
  const url = mediaUrl(env.assetURL, media.url_file);
  const estado = media.eliminacion ? media.eliminacion.estado : null;
  const marcada = !!estado;
  const eliminando = estado === 'deleting';
  const fallo = estado === 'error';
  const mensaje = (media.eliminacion && media.eliminacion.error) || MSG_ERROR_ELIMINACION;
  const tituloDeshacer = tituloDeshacerEliminar(fallo ? mensaje : null, disparador);
  const abrible = abrir && !!url && !canDelete && !marcada;

  const boxSx = {
    width: size,
    height: size,
    borderRadius: '12px',
    background: '#eef0f3',
    position: 'relative',
    overflow: 'hidden',
    flexShrink: 0,
    opacity: saliendo ? 0 : 1,
    transition: `opacity ${FADE_MS}ms ease`,
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
    ...(fallo ? { boxSizing: 'border-box', border: `2px solid ${ERROR_BORDER}` } : {}),
  };
  const linkProps = abrible ? { component: 'a', href: url, target: '_blank', rel: 'noopener noreferrer', title: 'Abrir imagen' } : {};

  return (
    <Box sx={boxSx} title={fallo ? mensaje : undefined} aria-busy={eliminando} {...linkProps}>
      {url ? (
        <img src={url} alt={alt} style={marcada ? dimmedImgStyle : imgStyle} />
      ) : (
        <div className="d-flex align-items-center justify-content-center h-100" style={{ color: '#aab' }}>
          <i className="fa fa-image fa-2x" />
        </div>
      )}
      {eliminando && (
        <Box sx={veilSx}>
          <CircularProgress size={24} aria-label="Eliminando imagen" sx={{ color: BRAND_GREEN }} />
        </Box>
      )}
      {canDelete && media.uuid && !marcada && (
        <Box
          component="button"
          type="button"
          aria-label="Eliminar imagen"
          title="Eliminar imagen"
          disabled={disabled || saliendo}
          onClick={() => onDelete(media.uuid)}
          sx={deleteBtnSx}>
          <i className="fa fa-trash" />
        </Box>
      )}
      {marcada && !eliminando && (
        <Box
          component="button"
          type="button"
          aria-label={tituloDeshacer}
          title={tituloDeshacer}
          disabled={disabled}
          onClick={() => onUndo(media.uuid)}
          sx={deleteBtnSx}>
          <i className="fa fa-undo" />
        </Box>
      )}
    </Box>
  );
};

export { FADE_MS };
export default ImagenGuardada;
